/**
 * The app shell and its (deliberately tiny) hash router.
 *
 * `#/` is the game — a human against a bot (ticket 06). `#/deck` is the deck builder
 * (ticket 23). `#/train` is the competitive training block (gauntlet, drills, the log). `#/stress` is the busy-board scene the 60fps claim is measured on. `#/about`
 * keeps ticket 01's workspace page reachable.
 *
 * Every route below the shell is lazily imported: the board and the builder both pull in the
 * 4.7 MB card dataset (and the board pulls Motion), and the entry bundle has no business
 * carrying either.
 */
import { Suspense, lazy, useEffect, useState } from 'react';

const GameScreen = lazy(async () => ({ default: (await import('./board/GameScreen.tsx')).GameScreen }));
const DeckBuilder = lazy(async () => ({ default: (await import('./deck/DeckBuilder.tsx')).DeckBuilder }));
const StressScene = lazy(async () => ({ default: (await import('./board/StressScene.tsx')).StressScene }));
const AboutPage = lazy(async () => ({ default: (await import('./AboutPage.tsx')).AboutPage }));
const TrainingPage = lazy(async () => ({
  default: (await import('./training/TrainingPage.tsx')).TrainingPage,
}));

export type Route = 'game' | 'deck' | 'train' | 'stress' | 'about';

export function routeFromHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, '').split('?')[0] ?? '';
  if (path === 'deck') return 'deck';
  if (path === 'train') return 'train';
  if (path === 'stress') return 'stress';
  if (path === 'about') return 'about';
  return 'game';
}

function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    routeFromHash(typeof location === 'undefined' ? '' : location.hash),
  );
  useEffect(() => {
    const onChange = () => {
      setRoute(routeFromHash(location.hash));
    };
    window.addEventListener('hashchange', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
    };
  }, []);
  return route;
}

export function App() {
  const route = useRoute();

  return (
    <Suspense fallback={<p className="booting">Dealing…</p>}>
      {route === 'game' ? <GameScreen showFps={new URLSearchParams(location.search).has('fps')} /> : null}
      {route === 'deck' ? <DeckBuilder /> : null}
      {route === 'train' ? <TrainingPage /> : null}
      {route === 'stress' ? <StressScene /> : null}
      {route === 'about' ? <AboutPage /> : null}
    </Suspense>
  );
}
